import React, { useState, useEffect } from 'react';
import { DataTable } from '@/components/DataTable';
import { invoicesService } from '@/services/invoices';
import { getApiErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus } from '@phosphor-icons/react';
import { formatCurrency, formatDate } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function BillingPage() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [newInv, setNewInv] = useState({
    invoiceNo: '',
    patient: '',
    amount: '',
    description: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await invoicesService.getAll();
        setInvoices(r.data);
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      }
    })();
  }, []);

  const columns = [
    { key: 'invoiceNo', header: 'Invoice #', sortable: true },
    { key: 'patient', header: 'Patient', sortable: true },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (value) => formatCurrency(value),
    },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <Badge variant={value === 'Paid' ? 'default' : 'secondary'}>
          {value}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">Billing</h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
            Manage invoices and payments
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-invoice-button">
              <Plus className="mr-2 h-4 w-4" weight="bold" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Invoice</DialogTitle>
              <DialogDescription>
                Generate a new invoice for patient
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="inv-no">Invoice #</Label>
                <Input
                  id="inv-no"
                  placeholder="INV-001"
                  value={newInv.invoiceNo}
                  onChange={(e) => setNewInv((i) => ({ ...i, invoiceNo: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-patient">Patient Name</Label>
                <Input
                  id="inv-patient"
                  placeholder="John Smith"
                  value={newInv.patient}
                  onChange={(e) => setNewInv((i) => ({ ...i, patient: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-amount">Amount ($)</Label>
                <Input
                  id="inv-amount"
                  type="number"
                  placeholder="450.00"
                  value={newInv.amount}
                  onChange={(e) => setNewInv((i) => ({ ...i, amount: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-desc">Description</Label>
                <Input
                  id="inv-desc"
                  placeholder="Consultation and tests"
                  value={newInv.description}
                  onChange={(e) => setNewInv((i) => ({ ...i, description: e.target.value }))}
                />
              </div>
              <Button
                className="w-full"
                onClick={async () => {
                  try {
                    const d = new Date().toISOString().split('T')[0];
                    await invoicesService.create({
                      invoiceNo: newInv.invoiceNo,
                      patient: newInv.patient,
                      amount: Number(newInv.amount) || 0,
                      date: d,
                      description: newInv.description || undefined,
                    });
                    toast.success('Invoice created successfully');
                    setIsDialogOpen(false);
                    setNewInv({ invoiceNo: '', patient: '', amount: '', description: '' });
                    const r = await invoicesService.getAll();
                    setInvoices(r.data);
                  } catch (error) {
                    toast.error(getApiErrorMessage(error));
                  }
                }}
              >
                Create Invoice
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable data={invoices} columns={columns} searchPlaceholder="Search invoices..." />
    </div>
  );
}